import { LOCALE_DEFINITIONS } from "@space/config/locales";

/**
 * Filter supported locales based on a search term.
 */
export function filterLocales(search: string) {
  const trimmed = search.trim().toLowerCase();
  if (!trimmed) {
    return [...LOCALE_DEFINITIONS];
  }
  return LOCALE_DEFINITIONS.filter(
    (l) =>
      l.label.toLowerCase().includes(trimmed) ||
      l.englishLabel.toLowerCase().includes(trimmed) ||
      l.code.toLowerCase().includes(trimmed),
  );
}

/**
 * Resolves a locale definition from its code.
 */
export function resolveLocaleDefinition(code: string) {
  return LOCALE_DEFINITIONS.find((l) => l.code === code) ?? LOCALE_DEFINITIONS[0];
}

/**
 * Trims the language catalog down to what the PLATFORM allows
 * (CONFIG_OVER_CODE, `i18n.available_locales` key from `GET /api/config`).
 *
 * The client REFLECTS, the server ENFORCES (CC-40): the sync write-path
 * ignores a `user.locale` outside policy even if the selector offered it.
 * This only avoids offering an option the server would silently discard.
 *
 * **Degrades safe, in all three directions**:
 *   - empty policy (the default) ⇒ the FULL catalog — this is the
 *     historical behavior, byte for byte;
 *   - policy that doesn't intersect the catalog (an admin typo, a code this
 *     app doesn't ship) ⇒ the FULL catalog, never an empty screen where the
 *     user can't even switch back to their own language;
 *   - codes with uppercase or spaces ⇒ normalized, not discarded.
 */
export function filterAllowedLocales<T extends { code: string }>(
  locales: readonly T[],
  availableLocales: readonly string[] | null | undefined,
): T[] {
  const allowed = new Set(
    (availableLocales ?? []).map((c) => c.trim().toLowerCase()).filter(Boolean),
  );
  if (allowed.size === 0) {
    return [...locales];
  }
  const trimmed = locales.filter((l) => allowed.has(l.code.toLowerCase()));
  // A policy with no intersection leaves the user with no option at all: it
  // is treated as broken configuration and the entire catalog is served.
  return trimmed.length > 0 ? trimmed : [...locales];
}
